'use client';

import React, { useState } from 'react';
import { cn } from 'common/utils';

export interface TabsType {
  className?: string;
  defaultTab?: string;
  onTabChange?: (tab: string) => void;
  children: React.ReactNode;
}

export interface TabsContainerType {
  className?: string;
  children: React.ReactNode;
}

export interface TabButtonType {
  tabKey: string;
  className?: string;
  children: React.ReactNode;
}

export interface TabContentType {
  tabKey: string;
  className?: string;
  children: React.ReactNode;
}

// Context for sharing tab state
const TabsContext = React.createContext<{
  activeTab: string;
  setActiveTab: (tab: string) => void;
}>({
  activeTab: 'recommendations',
  setActiveTab: () => null,
});

// Main Timeline Tabs Component
const Tabs = ({ defaultTab, onTabChange, children }: TabsType) => {
  const [activeTab, setActiveTab] = useState(defaultTab);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    onTabChange?.(tab);
  };

  const contextValue = {
    activeTab,
    setActiveTab: handleTabChange,
  };

  return (
    <TabsContext.Provider value={contextValue}>{children}</TabsContext.Provider>
  );
};

// Tab Container with Navigation
const TabsContainer: React.FC<TabsContainerType> = ({
  className,
  children,
}) => {
  return (
    <div
      className={cn('flex w-full border-b border-gray-200 bg-white', className)}
    >
      {children}
    </div>
  );
};

// Tab Button
const TabButton: React.FC<TabButtonType> = ({
  tabKey,
  className,
  children,
}) => {
  const { activeTab, setActiveTab } = React.useContext(TabsContext);
  const isActive = activeTab === tabKey;

  const handleClick = () => {
    setActiveTab(tabKey);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'relative flex-1 cursor-pointer border-none bg-transparent px-0 py-4 text-base font-semibold transition-all duration-300 ease-out',
        isActive
          ? 'text-[#000] opacity-100'
          : 'text-[#8E8E8E] opacity-100 hover:text-[#000]',
        className,
      )}
    >
      {children}
      {/* Border bottom for active tab */}
      <div
        className={cn(
          'absolute bottom-0 left-0 h-0.5 w-full bg-black-primary transition-all duration-300 ease-out',
          isActive ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0',
        )}
      />
    </button>
  );
};

// Tab Content
const TabContent: React.FC<TabContentType> = ({
  tabKey,
  className,
  children,
}) => {
  const { activeTab } = React.useContext(TabsContext);
  const isActive = activeTab === tabKey;

  return (
    <div
      className={cn(
        'transition-all duration-300 ease-out',
        isActive
          ? 'block translate-y-0 opacity-100'
          : 'hidden translate-y-2 opacity-0',
        className,
      )}
    >
      {isActive ? children : null}
    </div>
  );
};

export { TabButton, TabContent, Tabs, TabsContainer };
